import { Link } from "react-router";

import { InitialsTile } from "../lib/InitialsTile.tsx";
import type { UserCard } from "./shell.model.ts";

/**
 * Who is signed in, and whether the registry has verified them.
 *
 * The tick is decoration: `meta` already says "Verified" or "Not yet verified"
 * in words. An unverified account showing no tick and no words would be
 * indistinguishable from a rendering fault, and on a registry whose product is
 * verification that is the wrong ambiguity to leave.
 *
 * The initials sit in a square tile in the accent wash, not a grey circle. Every
 * other corner in ONDO is square, and the circle was the one place the shell
 * disagreed with itself.
 */
export function PortalUserCard({ user }: { user: UserCard }) {
  return (
    <Link className="shell__user" to={user.href}>
      <InitialsTile initials={user.initials} className="shell__user-initials" />
      <span className="shell__user-body">
        <span className="shell__user-name">{user.name}</span>
        <span
          className={`shell__user-meta${user.verified ? " shell__user-meta--verified" : ""}`}
        >
          {user.meta}
          {user.verified && (
            <span aria-hidden="true" className="shell__user-tick">
              ✓
            </span>
          )}
        </span>
        {/* The rung and the id, in an organisation. A third line rather than
            appended to the one above, because they answer different questions —
            what this person does here, and on what terms — and running them
            together makes a job title look like part of an identifier. */}
        {user.standing && <span className="shell__user-standing">{user.standing}</span>}
      </span>
      {/* The affordance the prototype's card carries, and the only thing that
          says this row is a door rather than a label. */}
      <span className="shell__user-chevron" aria-hidden="true">
        ›
      </span>
    </Link>
  );
}
